import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, AlertTriangle, Truck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import Footer from '../../components/Footer';
import { toast } from '../../hooks/use-toast';
import { useConfirmDialog } from '../../components/ui/confirm-dialog';

const CartPage = () => {
  const { cart, loading, updateCartItem, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [stockStatus, setStockStatus] = useState([]);
  const [checkingStock, setCheckingStock] = useState(false);
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  
  // Shipping settings from backend
  const [shippingSettings, setShippingSettings] = useState(null);
  
  // Fetch shipping settings
  useEffect(() => {
    const fetchShippingSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/shop/shipping-settings`);
        if (response.ok) {
          const data = await response.json();
          setShippingSettings(data);
        }
      } catch (error) {
        console.error('Error fetching shipping settings:', error);
      }
    };
    fetchShippingSettings();
  }, [API_BASE]);

  // Check stock availability when cart loads
  useEffect(() => {
    const checkStock = async () => {
      if (!cart.id || !cart.items.length) return;
      
      setCheckingStock(true);
      try {
        const res = await fetch(`${API_BASE}/api/cart/check-stock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cart_id: cart.id })
        });
        
        if (res.ok) {
          const data = await res.json();
          setStockStatus(data.items || []);
          
          // Show warnings for items that need adjustment
          data.items?.forEach(item => {
            if (item.needs_adjustment && item.available_qty > 0) {
              toast({
                title: 'Stock Limitation',
                description: `"${item.product_title}" has limited stock. Only ${item.available_qty} units available.`,
                variant: 'default'
              });
            } else if (item.available_qty === 0) {
              toast({
                title: 'Out of Stock',
                description: `"${item.product_title}" is out of stock. Please remove it from your cart.`,
                variant: 'destructive'
              });
            }
          });
        }
      } catch (error) {
        console.error('Error checking stock:', error);
      } finally {
        setCheckingStock(false);
      }
    };
    
    checkStock();
  }, [cart.id, cart.items.length, API_BASE]);

  const getStockInfo = (productId) => {
    return stockStatus.find(s => s.product_id === productId);
  };

  const handleQuantityChange = async (itemId, currentQuantity, change, productId) => {
    const newQuantity = currentQuantity + change;
    if (newQuantity < 1) return;
    
    // Check stock before increasing (frontend validation)
    const stockInfo = getStockInfo(productId);
    if (stockInfo && newQuantity > stockInfo.available_qty) {
      toast({
        title: 'Stok Yetersiz',
        description: `Bu üründen en fazla ${stockInfo.available_qty} adet satın alabilirsiniz`,
        variant: 'destructive'
      });
      return;
    }
    
    // Backend validation will also check stock
    const result = await updateCartItem(itemId, newQuantity);
    if (!result.success) {
      toast({
        title: 'Güncelleme Başarısız',
        description: result.error || 'Miktar güncellenemedi',
        variant: 'destructive'
      });
      // Refresh stock status after failed update
      const checkStockRes = await fetch(`${API_BASE}/api/cart/check-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cart_id: cart.id })
      });
      if (checkStockRes.ok) {
        const data = await checkStockRes.json();
        setStockStatus(data.items || []);
      }
    }
  };

  const handleRemove = async (itemId) => {
    const confirmed = await confirm({
      title: "Remove Item",
      description: "Remove this item from cart?",
      confirmText: "Remove",
      variant: "destructive"
    });
    
    if (confirmed) {
      await removeFromCart(itemId);
    }
  };

  if (loading && !cart.items.length) {
    return (
      <>
        <ResponsiveHeader />
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12">
          <div className="container mx-auto px-4">
            <p className="text-center text-gray-600">Loading cart...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <>
        <ResponsiveHeader />
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardContent className="p-12 text-center">
                <ShoppingCart className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                <h2 className="text-2xl font-bold mb-2 dark:text-white">Your cart is empty</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Add some products to get started!</p>
                <Button onClick={() => navigate('/shop')}>
                  Continue Shopping
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      {ConfirmDialogComponent}
      <ResponsiveHeader />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-red-600 to-black dark:from-red-500 dark:to-red-300 bg-clip-text text-transparent">
            Shopping Cart
          </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => {
              const stockInfo = getStockInfo(item.product_id);
              const isOutOfStock = stockInfo && stockInfo.available_qty === 0;
              const hasStockWarning = stockInfo && stockInfo.needs_adjustment;
              
              return (
              <Card key={item.id} className={`dark:bg-gray-800 dark:border-gray-700 ${isOutOfStock ? 'border-red-300 bg-red-50 dark:bg-red-900/30' : ''}`}>
                <CardContent className="p-4">
                  {/* Stock Warning Banner */}
                  {hasStockWarning && stockInfo.available_qty > 0 && (
                    <div className="mb-3 p-2 bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-semibold text-orange-800 dark:text-orange-300">
                          Insufficient stock for this item
                        </p>
                        <p className="text-orange-700 dark:text-orange-400">
                          Only {stockInfo.available_qty} units available. Please adjust quantity or it will be auto-adjusted at checkout.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {isOutOfStock && (
                    <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-semibold text-red-800">
                          Out of Stock
                        </p>
                        <p className="text-red-700">
                          This item is currently unavailable. Please remove it from your cart to proceed.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                      {item.image_url ? (
                        <img 
                          src={(() => {
                            const imgPath = item.image_url;
                            if (imgPath.startsWith('http')) return imgPath;
                            // Handle different URL formats for Kubernetes routing
                            let cleanPath = imgPath;
                            if (imgPath.startsWith('/uploads/')) {
                              // Legacy format: /uploads/xxx.jpg -> /api/uploads/general/xxx.jpg
                              cleanPath = imgPath.replace('/uploads/', '/api/uploads/general/');
                            } else if (!imgPath.startsWith('/api/')) {
                              // Unknown format: prepend /api
                              cleanPath = `/api${imgPath}`;
                            }
                            // If already starts with /api/, use as-is
                            return `${API_BASE}${cleanPath}`;
                          })()} 
                          alt={item.product_title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ShoppingCart className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex-1">
                      <Link 
                        to={`/shop/products/${item.product_slug}`}
                        className="font-semibold text-lg hover:text-red-600 dark:text-white dark:hover:text-red-400"
                      >
                        {item.product_title}
                      </Link>
                      {item.variant_title && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Variant: {item.variant_title}
                        </p>
                      )}
                      <p className="text-lg font-bold text-red-600 dark:text-red-400 mt-2">
                        ${item.price.toFixed(2)} CAD
                      </p>
                      {stockInfo && stockInfo.available_qty > 0 && stockInfo.available_qty <= 5 && (
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 font-medium">
                          Only {stockInfo.available_qty} left in stock
                        </p>
                      )}
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2 border dark:border-gray-600 rounded-lg">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity, -1, item.product_id)}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-lg disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
                          disabled={loading || isOutOfStock}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="px-3 font-semibold min-w-[40px] text-center dark:text-white">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity, 1, item.product_id)}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-lg disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
                          disabled={loading || isOutOfStock || (stockInfo && item.quantity >= stockInfo.available_qty)}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <p className="text-sm font-semibold dark:text-white">
                        Subtotal: ${(item.price * item.quantity).toFixed(2)} CAD
                      </p>

                      <button
                        onClick={() => handleRemove(item.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center gap-1 text-sm"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4 dark:bg-gray-800 dark:border-gray-700">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 dark:text-white">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Items ({cart.item_count})</span>
                    <span className="font-semibold dark:text-white">${cart.subtotal.toFixed(2)} CAD</span>
                  </div>
                  
                  <div className="border-t dark:border-gray-700 pt-3">
                    <div className="flex justify-between text-lg font-bold dark:text-white">
                      <span>Subtotal</span>
                      <span>${cart.subtotal.toFixed(2)} CAD</span>
                    </div>
                    
                    {/* Dynamic Shipping Info - Only show if shipping is enabled (threshold > 0 and flat_rate > 0) */}
                    {shippingSettings && shippingSettings.shipping_free_threshold > 0 && shippingSettings.shipping_flat_rate > 0 && (
                      <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Truck className="w-4 h-4 text-gray-500" />
                          {cart.subtotal >= shippingSettings.shipping_free_threshold ? (
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              You qualify for FREE shipping!
                            </span>
                          ) : (
                            <span className="text-gray-600 dark:text-gray-400">
                              Add ${(shippingSettings.shipping_free_threshold - cart.subtotal).toFixed(2)} more for free shipping
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {shippingSettings && shippingSettings.shipping_free_threshold > 0 && shippingSettings.shipping_flat_rate > 0
                        ? `Orders over $${shippingSettings.shipping_free_threshold.toFixed(0)} get free shipping. Taxes calculated at checkout.`
                        : 'Taxes calculated at checkout.'
                      }
                    </p>
                  </div>
                </div>

                {/* Check if any item is out of stock */}
                {stockStatus.some(s => s.available_qty === 0) && (
                  <div className="mb-3 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-sm text-red-800 dark:text-red-300">
                    <p className="font-semibold">Cannot proceed to checkout</p>
                    <p>Please remove out-of-stock items from your cart</p>
                  </div>
                )}
                
                <Button 
                  className="w-full mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => navigate('/checkout')}
                  disabled={loading || checkingStock || stockStatus.some(s => s.available_qty === 0)}
                >
                  {checkingStock ? 'Checking Stock...' : 'Proceed to Checkout'}
                </Button>
                
                <Button 
                  variant="outline"
                  className="w-full dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  onClick={() => navigate('/shop')}
                >
                  Continue Shopping
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CartPage;
